/**
 * Deterministic stand-in for Swift's `Int.random(in:)`.
 *
 * The Swift mocks roll like/comment/view counts at init, so every relaunch
 * showed different numbers. That is invisible on a phone you open once and very
 * visible in a demo: reload the Mini App in front of someone and the same event
 * has 43 likes, then 187. Seeding off a stable key makes the fixture behave like
 * data instead of noise, and keeps two people looking at the same screen.
 *
 * FNV-1a, then a xorshift step — cheap, and well-distributed enough that
 * neighbouring ids ("ev-1", "ev-2") don't produce neighbouring counts.
 */

function hash(key: string): number {
  let h = 0x811c9dc5;
  for (let i = 0; i < key.length; i++) {
    h ^= key.charCodeAt(i);
    h = Math.imul(h, 0x01000193);
  }
  return h >>> 0;
}

/** Inclusive on both ends, matching Swift's `a...b` range. */
export function seededInt(key: string, min: number, max: number): number {
  let x = hash(key) || 1;
  x ^= x << 13;
  x >>>= 0;
  x ^= x >> 17;
  x ^= x << 5;
  x >>>= 0;
  return min + (x % (max - min + 1));
}

export function seededBool(key: string): boolean {
  return seededInt(key, 0, 1) === 1;
}
